import React, { useState, useEffect, } from 'react';
import styled from 'styled-components';
import colors from 'theme/colors';
import TimeWarning from './TimeWarning';
import ReceiptForm from './ReceiptForm';
import { Column } from 'styled/alignment/Column';
import { AlertCircle, CreditCard as CreditCardIcon, Smartphone, Sparkles } from 'lucide-react';
import currency from 'helpers/currency';
import { Row } from 'styled/alignment/Row';
import CreditcardForm from './CreditcardForm';
import Text from 'components/_UI/Text';
import checkoutApi from 'api/requests/checkout';
import customFieldsApi from 'api/requests/customFields';
import { useCheckout } from 'context/checkout/Checkout';
import { useUser } from 'context/user/User';
import Loader from 'components/_UI/Loader';
import { FullyCentered } from 'styled/alignment/Center';
import PixForm from './PixForm';
import { useCollapse } from 'context/general/Collapse';
import PixCode from './PixCode';
import Modal from 'components/_UI/Modal';
import Button from 'components/_UI/Button';
import buttonText from 'texts/buttons/buttonText';
import UtilityHeader from 'components/_UI/UtilityHeader';
import { useEvent } from 'context/events/Event';
import { useNavigate, useSearchParams } from 'react-router-dom';
import routeParams from 'theme/routeParams';
import paths from 'routes/paths';
import {
  hasUniqueGuestError,
  uniqueGuestErrorMessage
} from '../commom/paymentFormUtils';
import PaymentSuccess from './PaymentSuccess';
import breakpoints from 'theme/breakpoints';
import { authenticate3DS, is3DSEnabled } from 'services/safe2pay3ds';
import { trackEvent } from 'scripts/pixels';


/**
 * Quanto o cliente economiza pagando no pix. Zero quando os dois meios custam
 * o mesmo — que é o caso de todo evento sem taxa negociada.
 */
function pixSavingsFrom(totalPriceByMethod) {
  const creditCard = Number(totalPriceByMethod?.creditcard);
  const pix = Number(totalPriceByMethod?.pix);

  if (!Number.isFinite(creditCard) || !Number.isFinite(pix)) {
    return 0;
  }

  return creditCard - pix > 0.001 ? creditCard - pix : 0;
}

export default function PaymentForm({ event: eventProp, tickets, totalPrice: totalPriceProp, totalPriceByMethod, onPixTrigger, onPaymentSuccess, skipSuccessModal }) {
  const navigate = useNavigate();
  const { checkout, setCheckout } = useCheckout();
  const { user } = useUser();
  const { collapse, setCollapse } = useCollapse();
  const [searchParams, setSearchParams] = useSearchParams();
  const { event: contextEvent } = useEvent();
  const event = eventProp || contextEvent;

  const [threeDSEnabled, setThreeDSEnabled] = useState(false);
  const [authenticating3DS, setAuthenticating3DS] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState([
    { name: 'pix', label: 'PIX', selected: true },
    { name: 'creditcard', label: 'Cartão de crédito', selected: false },
  ]);
  const [selected, setSelected] = useState(paymentMethod[0]);

  // A taxa do evento pode ser diferente entre cartão e pix, então o total
  // acompanha o meio escolhido. Sem `totalPriceByMethod` (fluxos que ainda
  // mandam só um valor) vale o total recebido, como antes.
  const totalPrice = totalPriceByMethod?.[selected.name] ?? totalPriceProp;

  // Quando a taxa negociada do pix é menor, o desconto é dito no próprio
  // momento da escolha — é a única hora em que a informação muda a decisão.
  const pixSavings = pixSavingsFrom(totalPriceByMethod);

  const [trigger, setTrigger] = useState(0);
  const [receiptFormData, setReceiptFormData] = useState(null);
  const [creditcardData, setCreditcardData] = useState(null);
  const [pixData, setPixData] = useState(null);
  const [loading, setLoading] = useState(null);
  const [creditcardError, setCreditcardError] = useState(null);
  const [successCreditCard, setSuccessCreditCardModal] = useState(false);
  const [time, setTime] = useState(null)
  const [errorRequest, setErrorRequest] = useState(0)
  const [refusedPayment, setRefusedPayment] = useState(false)
  const [customFields, setCustomFields] = useState([])

  function changePaymentMethod(selectedName) {

    const updatedMethods = paymentMethod.map(method =>
      method.name === selectedName
        ? { ...method, selected: true }
        : { ...method, selected: false }
    );

    setPaymentMethod(updatedMethods);

    const selectedMethod = updatedMethods.find(method => method.name === selectedName);
    setSelected(selectedMethod);
  }
  function onTriggerCreditcard(data) {
    setPixData(null);
    setCreditcardData(data);

    setTrigger(previousTrigger => previousTrigger + 1)

  }

  function onTriggerPix(data) {
    // order matters
    setPixData(data);
    setCreditcardData(null);

    setTrigger(previousTrigger => previousTrigger + 1);
  }

  function onReceiptFormCompleted(receiptForm) {
    setReceiptFormData(receiptForm);
    if (selected.name === 'pix') {
      onSubmitPix(pixData, receiptForm);
    }

    if (selected.name === 'creditcard') {
      onSubmitCreditcard(creditcardData, receiptForm);
    }
  }

  async function onSubmitCreditcard(creditcard, receiptForm) {
    if (!creditcard || !receiptForm) {
      return;
    }

    setLoading(true);
    setCreditcardError(null);

    let threeDSData = null;

    // 3DS Authentication (if enabled)
    if (threeDSEnabled) {
      try {
        setAuthenticating3DS(true);

        // Use totalPrice prop passed from CheckoutOrderMinimal
        const orderAmount = totalPrice || 0;

        threeDSData = await authenticate3DS(
          creditcard,
          receiptForm,
          user,
          orderAmount
        );
      } catch (error) {
        console.error('[Payment] 3DS authentication failed:', error);
        setAuthenticating3DS(false);
        setLoading(false);

        if (error.error === 'CARD_UNENROLLED') {
          setCreditcardError('Este cartao nao e elegivel para autenticacao 3DS. Por favor, tente outro cartao.');
        } else if (error.error === 'AUTHENTICATION_FAILED') {
          setCreditcardError('Falha na autenticacao do cartao. Verifique seus dados e tente novamente.');
        } else if (error.error === 'UNSUPPORTED_BRAND') {
          setCreditcardError('Bandeira do cartao nao suportada para autenticacao 3DS.');
        } else {
          setCreditcardError(error.message || 'Erro durante autenticacao. Tente novamente.');
        }

        setErrorRequest(errorRequest + 1);
        return;
      } finally {
        setAuthenticating3DS(false);
      }
    }

    const payload = createCreditcardPayload(creditcard, receiptForm, threeDSData);
    trackEvent('AddPaymentInfo', { value: totalPrice, orderId: checkout?.orderId });

    try {
      const { data } = await checkoutApi.checkoutOrder(payload, checkout?.orderId);

      if (data?.status === 'refused') {
        setRefusedPayment(data?.message)
        setErrorRequest(errorRequest + 1)
      }

      if (data?.status === 'approved') {
        trackEvent('Purchase', { value: totalPrice, orderId: checkout?.orderId, once: true });
        if (skipSuccessModal) {
          onPaymentSuccess?.();
        } else {
          setSuccessCreditCardModal(true);
        }
      }
    } catch (error) {
      if (hasUniqueGuestError(error)) {
        setCreditcardError(uniqueGuestErrorMessage)
      } else {
        setCreditcardError('Ocorreu um erro durante o pagamento. Por favor, tente novamente!')
      }
      setErrorRequest(errorRequest + 1)
    } finally {
      setReceiptFormData(false)
      setLoading(false);
    }
  }


  function createCustomerPayload(data) {
    const { firstName, lastName, phone, document, guests } = data;

    if (guests.length) {
      return {
        first_name: firstName,
        last_name: lastName,
        phone,
        document,
        guests
      }
    }

    return {
      first_name: firstName,
      last_name: lastName,
      phone,
      document,
      guests: []
    }
  }

  function createCreditcardPayload(creditcard, receipt, threeDSData = null) {
    const { creditcardNumber, date, code, holder, document, installments, grecaptcha_response } = creditcard;

    const payload = {
      payment_method: 'credit_card',
      installments: installments,
      grecaptcha_response: grecaptcha_response,
      customer: createCustomerPayload({ ...receipt, document }),
      card: {
        holder,
        number: creditcardNumber,
        expiration_date: date,
        security_code: code,
      }
    };

    // Add 3DS authentication data if available
    if (threeDSData) {
      payload.external_authentication = {
        cavv: threeDSData.cavv,
        xid: threeDSData.xid,
        eci: threeDSData.eci,
        version: threeDSData.version,
        reference_id: threeDSData.referenceId
      };
    }

    return payload;
  }

  function createPixPayload(data, receipt) {
    const { document } = data;

    return {
      payment_method: 'pix',
      customer: createCustomerPayload({ ...receipt, document })
    }
  }

  async function onSubmitPix(pixData, receiptForm) {
    if (!pixData || !receiptForm) {
      return;
    }

    setLoading(true);

    const payload = createPixPayload(pixData, receiptForm)
    trackEvent('AddPaymentInfo', { value: totalPrice, orderId: checkout?.orderId });

    setCheckout({
      ...checkout,
      payload
    })

    try {
      const { data } = await checkoutApi.checkoutOrder(payload, checkout?.orderId);

      setCheckout({ ...checkout, pix: data })
      onPixTrigger(() => (
        <PixCode
          time={time}
          tickets={tickets}
          totalPrice={totalPrice}
          onPaymentSuccess={onPaymentSuccess}
          skipSuccessModal={skipSuccessModal}
        />
      ))
      setCollapse({
        ...collapse,
        componentToRender: () => (
          <Column>
            <UtilityHeader title={'Pagamento'} onBack={goToOrder} />
            <PixCode
              time={time}
              tickets={tickets}
              totalPrice={totalPrice}
              onPaymentSuccess={onPaymentSuccess}
              skipSuccessModal={skipSuccessModal}
            />
          </Column>
        )
      })
    } catch (error) {
      if (hasUniqueGuestError(error)) {
        setCreditcardError(uniqueGuestErrorMessage)
      } else {
        setCreditcardError('Ocorreu um erro durante a compra! Tente novamente.')
      }
      setErrorRequest(errorRequest + 1)
    } finally {
      setReceiptFormData(false)
      setLoading(false);
    }
  }

  function goToWallet() {
    setSuccessCreditCardModal(false);
    navigate(paths.user.wallet);
  }

  function goToHome() {
    setSuccessCreditCardModal(false);
    onPaymentSuccess?.();
  }


  useEffect(() => {
    if (!event?.slug) {
      return undefined;
    }

    let isMounted = true;

    customFieldsApi.get(event.slug).then((response) => {
      if (isMounted) {
        setCustomFields((prev) => [...prev, ...response?.data?.data]);
      }
    });

    return () => { isMounted = false };
  }, [event?.slug])

  useEffect(() => {
    let cancelled = false;
    is3DSEnabled().then(enabled => {
      if (!cancelled) setThreeDSEnabled(enabled);
    });
    return () => { cancelled = true; };
  }, [])

  // Pagamento aprovado troca o conteúdo desta mesma superfície pela confirmação,
  // em vez de abrir uma camada sobre o formulário que acabou de ser preenchido.
  if (successCreditCard) {
    return (
      <FormContent as='div'>
        <PaymentSuccess
          variant='processing'
          title='Compra realizada com sucesso'
          amount={totalPrice}
          items={tickets}
          onClose={goToHome}
          onGoToWallet={goToWallet}
        />
      </FormContent>
    );
  }

  return (
    <>
      <FormContent>
        <TimeWarning setTime={setTime} onExpire={onPaymentSuccess} />

        <StepIndicator>
          <StepCircle active>1</StepCircle>
          <StepConnector />
          <StepCircle>2</StepCircle>
        </StepIndicator>

        <ReceiptForm
          trigger={trigger}
          tickets={tickets}
          customFields={customFields}
          onReceiptFormCompleted={onReceiptFormCompleted}
        />

        <PaymentSection>
          <SectionHeader>
            <SectionIcon>
              <CreditCardIcon size={16} color={colors.occasionalPurple} />
            </SectionIcon>
            <HeaderContent>
              <Text type='bold' paddingBottom={0} color={colors.nightDark}>
                Forma de pagamento
              </Text>
              <Text name='small' color={colors.night} paddingBottom={0} marginTop={'4px'}>
                Escolha sua forma de pagamento
              </Text>
            </HeaderContent>
          </SectionHeader>

          {
            loading ? (
              <FullyCentered height='200px'>
                <Column alignItems='center'>
                  <Loader />
                  {authenticating3DS && (
                    <Text marginTop='10px' textAlign='center' color={colors.night}>
                      Autenticando cartao...
                    </Text>
                  )}
                </Column>
              </FullyCentered>
            ) : (
              <Column>
                <PaymentMethodGrid>
                  {paymentMethod.map(el => (
                    <PaymentMethodCard
                      selected={el.selected}
                      key={el.name}
                      highlighted={el.name === 'pix' && pixSavings > 0}
                      onClick={() => changePaymentMethod(el.name)}
                    >
                      {el.name === 'pix' && pixSavings > 0 && (
                        <SavingsRibbon>
                          <Sparkles size={11} strokeWidth={2.5} />
                          Economize {currency.format(pixSavings)}
                        </SavingsRibbon>
                      )}
                      <MethodIcon selected={el.selected}>
                        {el.name === 'pix' ? (
                          <Smartphone size={22} color={el.selected ? colors.occasionalPurple : colors.night} />
                        ) : (
                          <CreditCardIcon size={22} color={el.selected ? colors.occasionalPurple : colors.night} />
                        )}
                      </MethodIcon>
                      <MethodLabel selected={el.selected}>
                        {el.label}
                      </MethodLabel>
                    </PaymentMethodCard>
                  ))}
                </PaymentMethodGrid>

                {/* No cartão, o cliente está a um clique de pagar mais caro. */}
                {selected.name === 'creditcard' && pixSavings > 0 && (
                  <SwitchToPix onClick={() => changePaymentMethod('pix')}>
                    <Sparkles size={14} strokeWidth={2.5} color={colors.successDark} />
                    <span>
                      Pagando com PIX você economiza <strong>{currency.format(pixSavings)}</strong>.
                      <SwitchToPixAction>Trocar para PIX</SwitchToPixAction>
                    </span>
                  </SwitchToPix>
                )}

                {
                  selected.name === 'creditcard' && (
                    <CreditcardForm onTrigger={(e) => onTriggerCreditcard(e)} setErrorRequest={setErrorRequest} errorRequest={errorRequest} tickets={tickets} totalPrice={totalPrice} />
                  )
                }

                {
                  selected.name === 'pix' && (
                    <PixForm onTrigger={(e) => onTriggerPix(e)} tickets={tickets} totalPrice={totalPrice} />
                  )

                }

                {creditcardError && (
                  <FullyCentered>
                    <Text color={colors.danger} textAlign={'center'}>
                      {creditcardError}
                    </Text>
                  </FullyCentered>
                )}
              </Column>
            )
          }
        </PaymentSection>
      </FormContent>
      <Modal isOpen={refusedPayment} onClose={() => setRefusedPayment(false)}>
        <WrapperModal>
          <Row fullyCentralized>
            <AlertCircle color={colors.danger} />
            <Text paddingBottom={'0'} marginLeft={'5px'}>{refusedPayment}</Text>
          </Row>
          <ContentButtonAgain>
            <Button marginTop={'15px'} variant='featured' onClick={() => setRefusedPayment(false)}>{buttonText.tryAgain}</Button>
          </ContentButtonAgain>
        </WrapperModal>
      </Modal>
    </>
  );
}

const FormContent = styled.form`
  width: 100%;
  padding: 10px 30px;
  background-color: ${colors.neutralLight};
  @media screen and (max-width: ${breakpoints.xm}) {
    padding: 10px 0;
  }

  @media screen and (min-width:  375px) and (max-width:  375px) {
    padding: 10px 15px;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
  gap: 0;
`;

const StepCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;

  ${({ active }) => active ? `
    background: ${colors.occasionalPurple};
    color: ${colors.neutralLight};
  ` : `
    background: ${colors.neutralDark};
    color: ${colors.night};
  `}
`;

const StepConnector = styled.div`
  width: 60px;
  height: 2px;
  background: ${colors.neutralDark};
`;

const PaymentSection = styled.div`
  background: ${colors.neutralLight};
  border: 1px solid ${colors.neutralDark};
  border-radius: 12px;
  padding: 24px 20px;
  margin-top: 24px;

  @media screen and (min-width: ${breakpoints.md}) {
    padding: 28px 28px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 20px;
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #F3EAFF;
  flex-shrink: 0;
  margin-top: 2px;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const PaymentMethodGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  /* espaço para a faixa de economia, que sobe para fora do card */
  margin: 10px 0 8px;
`;

const PaymentMethodCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 2px solid ${({ selected, highlighted }) => {
    if (selected) return colors.occasionalPurple;
    return highlighted ? colors.success : colors.neutralDark;
  }};
  background: ${({ selected }) => selected ? '#F3EAFF' : colors.neutralLight};
`;

const SavingsRibbon = styled.span`
  position: absolute;
  top: -11px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  background: ${colors.success};
  color: ${colors.neutralLight};
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
`;

const SwitchToPix = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  text-align: left;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${colors.success};
  background: #EFFBF3;
  color: ${colors.nightDark};
  font-size: 13px;
  line-height: 1.4;
  cursor: pointer;
  font-family: inherit;

  & > svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const SwitchToPixAction = styled.span`
  display: block;
  margin-top: 2px;
  color: ${colors.successDark};
  font-weight: 600;
  text-decoration: underline;
`;

const MethodIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ selected }) => selected ? colors.neutralLight : colors.neutralDark};
  margin-bottom: 8px;
`;

const MethodLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ selected }) => selected ? colors.occasionalPurple : colors.night};
`;

const WrapperModal = styled(Column)`
  padding: 10px;
  justify-content: center;
  align-items: center;
`;

const ContentButtonAgain = styled.div`
  width: 200px;
`;
